import type { Household } from './schemas/guest.js';
import { daysBetween } from './tasks.js';

/**
 * Gifts, and the thank-you card that owes them an answer.
 *
 * Three states, in the order they happen. `none` is the default and most of the
 * list: nothing has arrived, and nothing is owed.
 */
export type GiftStatus = 'none' | 'received' | 'thanked';

export function giftStatus(household: {
  giftReceivedAt?: string;
  thankYouSentAt?: string;
}): GiftStatus {
  if (household.thankYouSentAt) return 'thanked';
  if (household.giftReceivedAt) return 'received';
  return 'none';
}

/**
 * A card is thanked when it is sent, whatever the gift said.
 *
 * `thankYouSentAt` without `giftReceivedAt` is not a mistake worth correcting:
 * plenty of people write to somebody who simply came, and a list that refused to
 * record that would be wrong about the thing it exists to track.
 */
export const owesThankYou = (household: {
  giftReceivedAt?: string;
  thankYouSentAt?: string;
}): boolean => giftStatus(household) === 'received';

/**
 * Etiquette gives about three months, and the number everyone quotes is three
 * months rather than a year — the year is a myth, and by then the gift is
 * remembered and the card is not.
 *
 * Here so the list can say "waiting 94 days" rather than only "waiting", which
 * is the difference between a nudge and a number you act on.
 */
export const THANK_YOU_DAYS = 90;

export interface GiftStats {
  received: number;
  thanked: number;
  owed: number;
  /** Days the longest-waiting unanswered gift has been waiting; 0 when none. */
  longestWait: number;
  /** How many of those have passed the three months etiquette allows. */
  overdue: number;
}

export function giftStats(
  households: readonly Pick<Household, 'giftReceivedAt' | 'thankYouSentAt'>[],
  today: string,
): GiftStats {
  let received = 0;
  let thanked = 0;
  let owed = 0;
  let longestWait = 0;
  let overdue = 0;

  for (const household of households) {
    const status = giftStatus(household);
    if (status === 'none') continue;
    if (household.giftReceivedAt) received += 1;
    if (status === 'thanked') {
      thanked += 1;
      continue;
    }

    owed += 1;
    if (household.giftReceivedAt) {
      // A gift dated in the future is somebody typing next year by accident, not
      // a card that is already late.
      const waited = Math.max(0, daysBetween(household.giftReceivedAt, today));
      longestWait = Math.max(longestWait, waited);
      if (waited > THANK_YOU_DAYS) overdue += 1;
    }
  }

  return { received, thanked, owed, longestWait, overdue };
}
